import React, { lazy } from "react";
import { createBrowserRouter, Outlet } from "react-router";
import * as Sentry from "@sentry/react";
import { connect } from "react-redux";
import AppErrorBoundary from "./components/AppErrorBoundary";
import Login from "./scenes/login/components/Login";
import Signup from "./scenes/login/components/Signup";
import ClientSignup from "./scenes/signup-forms/components/ClientSignup";
import Thankyou from "./scenes/signup-forms/components/Thankyou";
import StripeCheckout from "./scenes/signup-forms/components/StripeCheckout";
import ForgotPassword from "./scenes/login/components/ForgotPassword";
import ResetPassword from "./scenes/login/components/ResetPassword";
import AddOrEditTM from "./scenes/triggered-messaging/components/AddOrEditTM";
import StripeSubscription from "./scenes/stripe-subscription";
import DownloadSupportedBrowser from "./scenes/misc/components/DownloadSupportedBrowser";
import DownloadApp from "./scenes/misc/components/DownloadApp";
import bowser from "bowser";
import App from "./containers/App";
import LazyComponent from "./components/LazyComponent";
import { storyListViewTypes } from "./scenes/stories/StoryListView";
import ReferralView from "./scenes/referral/referral";

// Conditional component wrapper
const ConditionalProgramsEditor = connect((state) => ({
  uses_new_style_workout_planning:
    state.me?.uses_new_style_workout_planning || false,
}))(({ uses_new_style_workout_planning, ...props }) => {
  if (uses_new_style_workout_planning) {
    return <LazyComponent component={ScheduleProgramEditor} {...props} />;
  }
  return <LazyComponent component={ProgramsEditor} {...props} />;
});

// Lazy load components
const ProgramsEditor = lazy(() => import("./scenes/programs/ProgramsEditor"));
const ScheduleProgramEditor = lazy(() =>
  import("./scenes/programs/ScheduleProgramEditor"),
);

const ExerciseEditor = lazy(() => import("./scenes/exercise/ExerciseEditor"));
const ProgramDayEditor = lazy(() =>
  import("./scenes/program-day/ProgramDayEditor"),
);
const ProgramPrintView = lazy(() =>
  import("./scenes/program-day/ProgramPrintView"),
);
const ClientsManager = lazy(() => import("./scenes/clients/ClientsManager"));
const GroupsManager = lazy(() => import("./scenes/groups/GroupsManager"));
const GroupMembers = lazy(() => import("./scenes/groups/GroupMembers"));
const AddNewMembers = lazy(() =>
  import("./scenes/groups/components/AddNewMembers"),
);
const TrainersManager = lazy(() => import("./scenes/trainers/TrainersManager"));
const TMManager = lazy(() => import("./scenes/triggered-messaging/TMManager"));
const AddGroupMessage = lazy(() =>
  import("./scenes/triggered-messaging/components/AddGroupMessage"),
);
const ExerciseListEditor = lazy(() =>
  import("./scenes/exercises/ExerciseListEditor"),
);
const TemplateEditor = lazy(() => import("./scenes/template/TemplateEditor"));
const ClientSettings = lazy(() =>
  import("./scenes/settings/components/ClientSettingsView"),
);
const ClientAccountSettings = lazy(() =>
  import("./scenes/settings/components/ClientAccountSettingsPage"),
);
const Assessments = lazy(() => import("./scenes/assessments"));
const AssessmentDetailView = lazy(() =>
  import("./scenes/assessments/AssessmentDetailView"),
);
const ExerciseProgressionList = lazy(() =>
  import("./scenes/assessments/ExerciseProgressionList"),
);
const ExerciseProgressionDetails = lazy(() =>
  import("./scenes/assessments/ExerciseProgressionDetails"),
);
const ProgressPhotosView = lazy(() =>
  import("./scenes/assessments/ProgressPhotosView"),
);
const CheckinsView = lazy(() => import("./scenes/assessments/CheckinsView"));
const NutritionView = lazy(() => import("./scenes/assessments/NutritionView"));
const EditNutritionGoal = lazy(() =>
  import("./scenes/assessments/EditNutritionGoal"),
);
const LeaderBoard = lazy(() => import("./scenes/leaderBoard"));
const LeaderBoardCheckinsView = lazy(() =>
  import("./scenes/leaderBoard/LeaderBoardCheckinsView"),
);
const LeaderBoardProgressPhotosView = lazy(() =>
  import("./scenes/leaderBoard/LeaderBoardProgressPhotosView"),
);
const LeaderBoardWeightView = lazy(() =>
  import("./scenes/leaderBoard/LeaderBoardWeightView"),
);
const LeaderBoardWorkoutLogged = lazy(() =>
  import("./scenes/leaderBoard/LeaderBoardWorkoutLogged"),
);
const ClientMessages = lazy(() =>
  import("./scenes/client-messages/ClientMessages"),
);
const AddOrEditMessage = lazy(() =>
  import("./scenes/client-messages/components/AddOrEditMessage"),
);
const AddOrEditGroupView = lazy(() =>
  import("./scenes/groups/components/AddOrEditGroupView"),
);
const PostView = lazy(() => import("./scenes/post/PostView"));
const SignupFormListView = lazy(() =>
  import("./scenes/signup-forms/SignupFormListView"),
);
const EditSignupForm = lazy(() =>
  import("./scenes/signup-forms/EditSignupForm"),
);
const FileGeneratorView = lazy(() =>
  import("./scenes/branded-app/FileGeneratorView"),
);
const StripeConnectView = lazy(() => import("./scenes/stripe"));
const SettingsView = lazy(() => import("./scenes/settings"));
const BillingView = lazy(() => import("./scenes/settings/BillingView"));
const BillingEditView = lazy(() => import("./scenes/settings/BillingEditView"));
const IntegrationsView = lazy(() =>
  import("./scenes/settings/IntegrationsView"),
);
const StripeSettings = lazy(() => import("./scenes/settings/StripeSettings"));
const LinksView = lazy(() => import("./scenes/settings/LinksView"));
const AddNewLink = lazy(() =>
  import("./scenes/settings/components/AddNewLink"),
);
const AppSettingsView = lazy(() =>
  import("./scenes/settings/components/AppSettingsView"),
);
const NewsFeedSettingsView = lazy(() =>
  import("./scenes/settings/NewsFeedSettings"),
);
const ClientNewsFeedSettingsView = lazy(() =>
  import("./scenes/settings/ClientNewsFeedSettings"),
);
const TrainerNewsFeedSettingsView = lazy(() =>
  import("./scenes/settings/TrainerNewsFeedSettings"),
);
const InAppPurchaseSettings = lazy(() =>
  import("./scenes/settings/InAppPurchaseSettings"),
);
const AutoResponders = lazy(() => import("./scenes/auto-responders"));
const ARManager = lazy(() =>
  import("./scenes/auto-responders/components/ARManager"),
);
const AddOrEditResponderMessage = lazy(() =>
  import("./scenes/auto-responders/components/AddOrEditResponderMessage"),
);
const Habits = lazy(() => import("./scenes/habits"));
const AddOrEditHabit = lazy(() =>
  import("./scenes/habits/components/AddOrEditHabit"),
);
const ClientHabits = lazy(() => import("./scenes/habits/ClientHabits"));
const LanguageSettings = lazy(() =>
  import("./scenes/settings/LanguageSettings"),
);
const StoryListView = lazy(() => import("./scenes/stories/StoryListView"));

const isPhone = typeof bowser.mobile !== "undefined";
const isBrowserSupported =
  bowser.chrome === true || bowser.firefox === true || bowser.safari === true;
const token = new URL(window.location.href).searchParams.get("token");

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <AppErrorBoundary />,
    children: [
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/download-app",
    element: <DownloadApp />,
  },
  {
    path: "/signup/:ticker/thankyou/:token",
    element: <LazyComponent component={Thankyou} />,
    loader: ({ params }) => ({
      ticker: params.ticker,
      token: params.token,
    }),
  },
  {
    path: "/signup/:ticker/thankyou",
    element: <LazyComponent component={Thankyou} />,
    loader: ({ params }) => ({
      ticker: params.ticker,
    }),
  },
  {
    path: "/signup/:ticker/checkout",
    element: <LazyComponent component={StripeCheckout} />,
    loader: ({ params }) => ({
      ticker: params.ticker,
    }),
  },
  {
    path: "/signup/:ticker",
    element: <LazyComponent component={ClientSignup} />,
    loader: ({ params, request }) => {
      const url = new URL(request.url);
      return {
        ticker: params.ticker,
        email: url.searchParams.get("email") || "",
        referral: url.searchParams.get("ref") || "",
      };
    },
  },
  {
    path: "/previewForm",
    element: <LazyComponent component={ClientSignup} preview={true} />,
  },
  {
    path: "/signup",
    element: <Signup />,
  },
  {
    path: "/client-portal",
    element: <StripeSubscription />,
  },
  ...(isBrowserSupported && isPhone && token
    ? [
        {
          path: "/auto-messaging/:groupId/new",
          element: <LazyComponent component={AddOrEditTM} />,
          loader: ({ params }) => ({
            groupId: params.groupId,
          }),
        },
      ]
    : []),
  // Main app routes
  {
    path: "/",
    element: <App />,
    children: [
      {
        index: true,
        element: <LazyComponent component={ClientsManager} />,
      },
      {
        path: "clients",
        element: <LazyComponent component={ClientsManager} />,
      },
      {
        path: "referrals",
        element: <ReferralView />,
      },
      {
        path: "stories",
        element: (
          <LazyComponent
            component={StoryListView}
            key="news"
            viewType={storyListViewTypes.FOR_NEWS}
          />
        ),
      },
      {
        path: "stories/:storyId",
        element: (
          <LazyComponent
            component={StoryListView}
            key="single-story"
            viewType={storyListViewTypes.FOR_SINGLE_STORY}
          />
        ),
        loader: ({ params }) => ({ storyId: params.storyId }),
      },
      {
        path: "exercises",
        element: <LazyComponent component={ExerciseListEditor} />,
      },
      {
        path: "exercises/:exerciseId",
        element: <LazyComponent component={ExerciseEditor} />,
        loader: ({ params }) => ({ exerciseId: parseInt(params.exerciseId) }),
      },
      {
        path: "workout-templates",
        element: (
          <LazyComponent
            component={TemplateEditor}
            forWorkoutTemplates={true}
          />
        ),
      },
      {
        path: "plan-templates",
        element: (
          <LazyComponent
            component={TemplateEditor}
            forWorkoutTemplates={false}
          />
        ),
      },
      {
        path: "plan-templates/:orderedProgramListId",
        element: (
          <LazyComponent
            component={ProgramsEditor}
            key="plan-templates"
            forClientPrograms={false}
          />
        ),
        loader: ({ params }) => ({
          orderedProgramListId: parseInt(params.orderedProgramListId),
        }),
      },
      {
        path: "groups",
        element: <LazyComponent component={GroupsManager} />,
      },
      {
        path: "groups/new",
        element: <LazyComponent component={AddOrEditGroupView} />,
      },
      {
        path: "groups/:groupId/edit",
        element: <LazyComponent component={AddOrEditGroupView} />,
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "groups/:groupId/members",
        element: (
          <LazyComponent
            component={GroupMembers}
            forAutoResponder={false}
            forHabits={false}
          />
        ),
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "groups/:groupId/members/new",
        element: (
          <LazyComponent
            component={AddNewMembers}
            forAutoResponder={false}
            forHabits={false}
          />
        ),
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "groups/:groupId/files-links",
        element: <LazyComponent component={LinksView} />,
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "trainers",
        element: <LazyComponent component={TrainersManager} />,
      },
      {
        path: "settings",
        element: <LazyComponent component={SettingsView} />,
      },
      {
        path: "settings/billing",
        element: <LazyComponent component={BillingView} />,
      },
      {
        path: "settings/billing/edit",
        element: <LazyComponent component={BillingEditView} />,
        loader: ({ request }) => {
          const url = new URL(request.url);
          return {
            newPlan: url.searchParams.get("new_plan")
              ? parseInt(url.searchParams.get("new_plan"))
              : false,
            newStatus: url.searchParams.get("new_status")
              ? parseInt(url.searchParams.get("new_status"))
              : false,
            newCC: url.searchParams.get("new_cc")
              ? parseInt(url.searchParams.get("new_cc"))
              : false,
          };
        },
      },
      {
        path: "settings/integrations",
        element: <LazyComponent component={IntegrationsView} />,
      },
      {
        path: "settings/app-links",
        element: <LazyComponent component={LinksView} />,
      },
      {
        path: "settings/app-links/new",
        element: <LazyComponent component={AddNewLink} />,
      },
      {
        path: "settings/app-settings",
        element: <LazyComponent component={AppSettingsView} />,
      },
      {
        path: "settings/payment",
        element: <LazyComponent component={StripeSettings} />,
      },
      {
        path: "settings/news-feed",
        element: <LazyComponent component={NewsFeedSettingsView} />,
      },
      {
        path: "settings/news-feed/clients",
        element: <LazyComponent component={ClientNewsFeedSettingsView} />,
      },
      {
        path: "settings/news-feed/trainers",
        element: <LazyComponent component={TrainerNewsFeedSettingsView} />,
      },
      {
        path: "settings/in-app-purchase",
        element: <LazyComponent component={InAppPurchaseSettings} />,
      },
      {
        path: "settings/language",
        element: <LazyComponent component={LanguageSettings} />,
      },
      {
        path: "auto-responders",
        element: <LazyComponent component={AutoResponders} />,
      },
      {
        path: "auto-responders/:groupId/members",
        element: (
          <LazyComponent
            component={GroupMembers}
            forAutoResponder={true}
            forHabits={false}
          />
        ),
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "auto-responders/:groupId/members/new",
        element: (
          <LazyComponent
            component={AddNewMembers}
            forAutoResponder={true}
            forHabits={false}
          />
        ),
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "auto-responders/:groupId/messages",
        element: <LazyComponent component={ARManager} />,
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "auto-responders/:groupId/messages/new",
        element: <LazyComponent component={AddOrEditResponderMessage} />,
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "auto-responders/:groupId/:messageId/edit",
        element: <LazyComponent component={AddOrEditResponderMessage} />,
        loader: ({ params }) => ({
          groupId: params.groupId,
          messageId: params.messageId,
        }),
      },
      {
        path: "habits",
        element: <LazyComponent component={Habits} />,
      },
      {
        path: "habits/new",
        element: <LazyComponent component={AddOrEditHabit} />,
      },
      {
        path: "habits/:habitId/edit",
        element: <LazyComponent component={AddOrEditHabit} />,
        loader: ({ params }) => ({ habitId: params.habitId }),
      },
      {
        path: "habits/:groupId/members",
        element: (
          <LazyComponent
            component={GroupMembers}
            forAutoResponder={false}
            forHabits={true}
          />
        ),
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "habits/:groupId/members/new",
        element: (
          <LazyComponent
            component={AddNewMembers}
            forAutoResponder={false}
            forHabits={true}
          />
        ),
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "habits/:groupId/reports",
        element: <LazyComponent component={LeaderBoardCheckinsView} />,
        loader: ({ params }) => ({
          groupId: params.groupId ? parseInt(params.groupId) : undefined,
        }),
      },
      {
        path: "leaderBoard",
        element: <LazyComponent component={LeaderBoard} />,
        loader: ({ params }) => ({
          groupId: params.groupId ? parseInt(params.groupId) : undefined,
        }),
      },
      {
        path: "leaderBoard/:groupId",
        element: <LazyComponent component={LeaderBoard} />,
        loader: ({ params }) => ({ groupId: parseInt(params.groupId) }),
      },
      {
        path: "leaderBoard/assessments/:type/:id",
        element: <LazyComponent component={LeaderBoardWeightView} />,
        loader: ({ params }) => ({
          assessmentType: params.type,
          assessmentId: parseInt(params.id),
          groupId: params.groupId ? parseInt(params.groupId) : undefined,
        }),
      },
      {
        path: "leaderBoard/:groupId/assessments/:type/:id",
        element: <LazyComponent component={LeaderBoardWeightView} />,
        loader: ({ params }) => ({
          assessmentType: params.type,
          assessmentId: parseInt(params.id),
          groupId: parseInt(params.groupId),
        }),
      },
      {
        path: "leaderBoard/assessments/checkins",
        element: <LazyComponent component={LeaderBoardCheckinsView} />,
        loader: ({ params }) => ({
          groupId: params.groupId ? parseInt(params.groupId) : undefined,
        }),
      },
      {
        path: "leaderBoard/:groupId/assessments/checkins",
        element: <LazyComponent component={LeaderBoardCheckinsView} />,
        loader: ({ params }) => ({ groupId: parseInt(params.groupId) }),
      },
      {
        path: "leaderBoard/assessments/progress_photos",
        element: <LazyComponent component={LeaderBoardProgressPhotosView} />,
      },
      {
        path: "leaderBoard/assessments/workoutLogged",
        element: <LazyComponent component={LeaderBoardWorkoutLogged} />,
        loader: () => ({ groupId: undefined }),
      },
      {
        path: "leaderBoard/:groupId/assessments/workoutLogged",
        element: <LazyComponent component={LeaderBoardWorkoutLogged} />,
        loader: ({ params }) => ({ groupId: parseInt(params.groupId) }),
      },
      {
        path: "auto-messaging/:groupId",
        element: <LazyComponent component={TMManager} />,
        loader: ({ params }) => ({
          groupId: params.groupId,
          clientId: params.clientId,
        }),
      },
      {
        path: "auto-messaging/clients/:clientId",
        element: <LazyComponent component={TMManager} />,
        loader: ({ params }) => ({
          clientId: params.clientId,
          groupId: params.groupId,
        }),
      },
      {
        path: "auto-messaging/:groupId/new",
        element: <LazyComponent component={AddOrEditTM} />,
        loader: ({ params }) => ({
          groupId: params.groupId,
          messageId: params.messageId,
        }),
      },
      {
        path: "auto-messaging/:groupId/group-message",
        element: <LazyComponent component={AddGroupMessage} />,
        loader: ({ params }) => ({ groupId: params.groupId }),
      },
      {
        path: "auto-messaging/:messageId/edit",
        element: <LazyComponent component={AddOrEditTM} />,
        loader: ({ params }) => ({ messageId: params.messageId }),
      },
      {
        path: "posts/:postId",
        element: <LazyComponent component={PostView} />,
        loader: ({ params }) => ({ postId: params.postId }),
      },
      {
        path: "forms",
        element: <LazyComponent component={SignupFormListView} />,
      },
      {
        path: "forms/:ticker",
        element: <LazyComponent component={EditSignupForm} />,
        loader: ({ params }) => ({
          ticker: params.ticker === "new" ? null : params.ticker,
        }),
      },
      {
        path: "stripe",
        element: <LazyComponent component={StripeConnectView} />,
      },
      {
        path: "branded-app-files",
        element: <LazyComponent component={FileGeneratorView} />,
        loader: ({ request }) => {
          const url = new URL(request.url);
          return { appId: url.searchParams.get("app_id") || "my_app" };
        },
      },
      // Client-specific routes
      {
        path: "clients/:clientId/programs",
        element: (
          <ConditionalProgramsEditor
            forClientPrograms={true}
            key="client-plans"
          />
        ),
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/programs/past",
        element: (
          <LazyComponent
            component={ProgramsEditor}
            forClientPrograms={true}
            forCurrentAndUpcoming={false}
            key="past-plans"
          />
        ),
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/diary",
        element: (
          <LazyComponent
            component={StoryListView}
            key="diary"
            viewType={storyListViewTypes.FOR_CLIENT_DIARY}
          />
        ),
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/settings",
        element: <LazyComponent component={ClientSettings} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/settings/account",
        element: <LazyComponent component={ClientAccountSettings} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments",
        element: <LazyComponent component={Assessments} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments/:type/:id",
        element: <LazyComponent component={AssessmentDetailView} />,
        loader: ({ params }) => ({
          assessmentType: params.type,
          assessmentId: parseInt(params.id),
          clientId: parseInt(params.clientId),
        }),
      },
      {
        path: "clients/:clientId/assessments/checkins",
        element: <LazyComponent component={CheckinsView} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/nutrition",
        element: <LazyComponent component={NutritionView} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments/nutrition",
        element: <LazyComponent component={NutritionView} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments/edit-nutrition",
        element: <LazyComponent component={EditNutritionGoal} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments/progress_photos",
        element: <LazyComponent component={ProgressPhotosView} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments/exercise",
        element: <LazyComponent component={ExerciseProgressionList} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/assessments/exercise/progression/:id",
        element: <LazyComponent component={ExerciseProgressionDetails} />,
        loader: ({ params }) => ({
          clientId: parseInt(params.clientId),
          exerciseId: parseInt(params.id),
        }),
      },
      {
        path: "clients/:clientId/messages",
        element: <LazyComponent component={ClientMessages} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/messages/new",
        element: <LazyComponent component={AddOrEditMessage} />,
        loader: ({ params }) => ({
          clientId: parseInt(params.clientId),
          messageId: undefined,
        }),
      },
      {
        path: "clients/:clientId/messages/:messageId/edit",
        element: <LazyComponent component={AddOrEditMessage} />,
        loader: ({ params }) => ({
          clientId: parseInt(params.clientId),
          messageId: params.messageId ? parseInt(params.messageId) : undefined,
        }),
      },
      {
        path: "clients/:clientId/habits",
        element: <LazyComponent component={ClientHabits} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      {
        path: "clients/:clientId/app-links",
        element: <LazyComponent component={LinksView} />,
        loader: ({ params }) => ({ clientId: parseInt(params.clientId) }),
      },
      // Program day routes
      {
        path: "client/:clientId/programs/:programId/days/:day/date/:date",
        element: <LazyComponent component={ProgramDayEditor} />,
        loader: ({ params, request }) => {
          const url = new URL(request.url);
          return {
            clientId: parseInt(params.clientId),
            programId: parseInt(params.programId),
            day: params.day,
            date: params.date,
            newExerciseId: url.searchParams.get("new_exercise_id")
              ? parseInt(url.searchParams.get("new_exercise_id"))
              : false,
          };
        },
      },
      {
        path: "programs/:programId/days/:day/date/:date",
        element: <LazyComponent component={ProgramDayEditor} />,
        loader: ({ params, request }) => {
          const url = new URL(request.url);
          return {
            programId: parseInt(params.programId),
            day: params.day,
            date: params.date,
            newExerciseId: url.searchParams.get("new_exercise_id")
              ? parseInt(url.searchParams.get("new_exercise_id"))
              : false,
          };
        },
      },
      {
        path: "programs/:programId/print",
        element: <LazyComponent component={ProgramPrintView} />,
        loader: ({ params }) => ({ programId: parseInt(params.programId) }),
      },
    ],
  },
  // Fallback for unsupported browsers
  {
    path: "*",
    element: (
      <div>
        {!isBrowserSupported && <DownloadSupportedBrowser />}
        {isBrowserSupported && isPhone && <DownloadApp />}
        {isBrowserSupported && !isPhone && <App />}
      </div>
    ),
  },
    ],
  },
], { basename: "/web" });
